import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { startRealtime, stopRealtime } from "@/lib/realtime";
import { toast } from "sonner";

const AuthContext = createContext(null);

// Friendly labels for realtime toasts
const ENTITY_LABEL = {
  trucks: "truck",
  trailers: "trailer",
  drivers: "driver",
  repairs: "repair",
  trips: "trip",
  city_shifts: "city shift",
};

const ACTION_VERB = { created: "added", updated: "updated", deleted: "removed" };

const ENTITY_ICON = {
  trucks: "🚛", trailers: "🚚", drivers: "🧑‍✈️",
  repairs: "🔧", trips: "🗺️", city_shifts: "🏙️",
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        const tok = sessionStorage.getItem("fleet_token") || localStorage.getItem("fleet_token");
        if (tok) startRealtime(tok);
      } catch {
        setUser(null);
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  // Global realtime toast — only for changes made by OTHER users so the local
  // actor doesn't see a duplicate confirmation.
  useEffect(() => {
    if (!user) return;
    const h = (e) => {
      const d = e?.detail;
      if (!d?.entity || d.entity === "system") return;
      if (d.actor_id && d.actor_id === user.id) return;  // silence own actions
      // Messages get a louder, sender-aware toast
      if (d.entity === "messages" && d.action === "created") {
        toast("💬 New message received", {
          description: "Check the Messages panel",
          duration: 4500,
          position: "top-right",
        });
        return;
      }
      if (d.entity === "messages") return;  // ignore read/delete noise
      if (d.entity === "geofence_events") return;  // NotificationBell owns geofence toasts
      const label = ENTITY_LABEL[d.entity] || d.entity;
      const verb = ACTION_VERB[d.action] || d.action;
      const icon = ENTITY_ICON[d.entity] || "🔔";
      toast(`${icon} A ${label} was ${verb}`, { duration: 3000, position: "bottom-right" });
    };
    window.addEventListener("rf-data-change", h);
    return () => window.removeEventListener("rf-data-change", h);
  }, [user]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) sessionStorage.setItem("fleet_token", data.token);
    setUser(data.user);
    if (data.token) startRealtime(data.token);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed:", e?.message || e);
    }
    stopRealtime();
    sessionStorage.removeItem("fleet_token");
    localStorage.removeItem("fleet_token");
    setUser(null);
  };

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  const hasPermission = useCallback(
    (module, action = "view") => {
      if (!user) return false;
      if (user.role === "admin") return true;
      const perms = user.permissions || {};
      return Boolean(perms?.[module]?.[action]);
    },
    [user]
  );

  return (
    <AuthContext.Provider value={{ user, checking, login, logout, setUser, refreshMe, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export { formatApiErrorDetail };
